import React from 'react';
import type { AlertsFilters, AlertStatus, AlertBucket } from '../hooks/useJurisprudenceAlerts';

interface Props {
  value: AlertsFilters;
  onChange: (next: AlertsFilters) => void;
  onReset: () => void;
}

const STATUS_OPTIONS: Array<{ value: AlertStatus; label: string }> = [
  { value: 'sent', label: 'Envoyée' },
  { value: 'pending', label: 'En attente' },
  { value: 'failed', label: 'Échec' },
  { value: 'suppressed', label: 'Supprimée' },
];

const BUCKET_OPTIONS: Array<{ value: AlertBucket; label: string }> = [
  { value: 'immediate', label: 'Immédiat' },
  { value: 'daily', label: 'Quotidien' },
  { value: 'weekly', label: 'Hebdomadaire' },
];

const selectClass =
  'w-full px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-mcdm-third-400 text-mcdm-third-500 dark:text-mcdm-primary-100 focus:outline-none focus:ring-2 focus:ring-mcdm-third-300';

const labelClass = 'block text-xs font-medium text-mcdm-third-500 dark:text-mcdm-primary-200 mb-1';

const AlertFilters: React.FC<Props> = ({ value, onChange, onReset }) => {
  const updateField = <K extends keyof AlertsFilters>(field: K, val: AlertsFilters[K]) => {
    const next = { ...value, [field]: val || undefined };
    onChange(next);
  };

  return (
    <div className="bg-white dark:bg-mcdm-third-400 border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <div>
          <label className={labelClass}>Statut</label>
          <select
            value={value.status ?? ''}
            onChange={e =>
              updateField('status', (e.target.value || undefined) as AlertStatus | undefined)
            }
            className={selectClass}
          >
            <option value="">Tous</option>
            {STATUS_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelClass}>Fréquence</label>
          <select
            value={value.bucket ?? ''}
            onChange={e =>
              updateField('bucket', (e.target.value || undefined) as AlertBucket | undefined)
            }
            className={selectClass}
          >
            <option value="">Toutes</option>
            {BUCKET_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelClass}>Du</label>
          <input
            type="date"
            value={value.dateFrom ?? ''}
            onChange={e => updateField('dateFrom', e.target.value || undefined)}
            className={selectClass}
          />
        </div>

        <div>
          <label className={labelClass}>Au</label>
          <input
            type="date"
            value={value.dateTo ?? ''}
            onChange={e => updateField('dateTo', e.target.value || undefined)}
            className={selectClass}
          />
        </div>

        <div className="flex items-end">
          <button
            type="button"
            onClick={onReset}
            className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-mcdm-third-500 dark:text-mcdm-primary-100 hover:bg-gray-50 dark:hover:bg-mcdm-third-500 transition-colors"
          >
            Réinitialiser
          </button>
        </div>
      </div>
    </div>
  );
};

export default AlertFilters;
